import { supabase } from "@/integrations/supabase/client";
import { TaskAssignee } from "@/types/kanban";
import { createDefaultSubtasksForMFO } from "./subtaskUtils";

export const fetchMFOs = async (departmentId?: string) => {
  try {
    let query = supabase
      .from("mfo_items")
      .select(
        `
        id,
        code,
        title,
        budget,
        priority,
        due_date,
        status,
        q1,
        q2,
        q3,
        q4,
        progress,
        departments (
          id,
          name
        ),
        mfo_success_indicators (
          indicator
        ),
        mfo_assignees (
          employees (
            id,
            name,
            avatar
          )
        )
      `
      )
      .order("created_at", { ascending: false });

    if (departmentId) {
      query = query.eq("department_id", departmentId);
    }

    const { data, error } = await query;

    if (error) throw error;

    const mfos = data.map((mfo) => {
      return {
        id: mfo.id,
        code: mfo.code,
        title: mfo.title,
        budget: mfo.budget,
        priority: mfo.priority,
        dueDate: mfo.due_date,
        status: mfo.status,
        progress: mfo.progress || 0,
        timeline: {
          q1: mfo.q1,
          q2: mfo.q2,
          q3: mfo.q3,
          q4: mfo.q4,
        },
        departmentId: mfo.departments?.id,
        departmentName: mfo.departments?.name,
        successIndicators: mfo.mfo_success_indicators?.map(
          (indicator) => indicator.indicator
        ),
        collaborators: mfo.mfo_assignees?.map((assignee) => ({
          id: assignee.employees?.id,
          name: assignee.employees?.name,
          avatar: assignee.employees?.avatar,
        })),
      };
    });

    return mfos;
  } catch (error) {
    console.error("Error fetching MFOs:", error);
    throw error;
  }
};

export const createMFO = async (mfo: {
  code: string;
  title: string;
  departmentId: string;
  budget: number;
  priority: "high" | "medium" | "low" | string;
  dueDate: string;
  status?: "not-started" | "in-progress" | "delayed" | "completed" | string;
  successIndicators?: string[];
  timeline?: { q1: boolean; q2: boolean; q3: boolean; q4: boolean };
  collaborators?: TaskAssignee[];
  progress?: number;
  subtasks?: { text: string; completed: boolean }[];
}): Promise<string> => {
  try {

    
    // First, insert the MFO
    const { data, error } = await supabase
      .from("mfo_items")
      .insert({
        code: mfo.code,
        title: mfo.title,
        department_id: mfo.departmentId,
        budget: mfo.budget,
        priority: mfo.priority || "medium",
        due_date: mfo.dueDate,
        status: mfo.status || "not-started",
        progress: mfo.progress || 0,
        q1: mfo.timeline?.q1 || false,
        q2: mfo.timeline?.q2 || false,
        q3: mfo.timeline?.q3 || false,
        q4: mfo.timeline?.q4 || false,
      })
      .select()
      .single();

    if (error) {
      console.error("Error in MFO insert:", error);
      throw error;
    }

    

    // Get the corresponding Kanban column based on status
    const statusToColumnMap: Record<string, string> = {
      "not-started": "Not Started",
      "in-progress": "In Progress",
      "delayed": "Delayed",
      "completed": "Completed"
    };

    const columnName = statusToColumnMap[mfo.status || "not-started"] || "Not Started";
   

    // Retrieve the column ID for the status
    const { data: columns, error: columnError } = await supabase
      .from("kanban_columns")
      .select("id, title")
      .ilike("title", `%${columnName}%`)
      .limit(1);

    if (columnError) {
      console.error("Error finding kanban column:", columnError);
      throw columnError;
    }
    
    if (!columns || columns.length === 0) {
      console.error("No kanban columns found matching:", columnName);
      throw new Error("Kanban column not found");
    }


    // Create the Kanban card
    const description = mfo.successIndicators && mfo.successIndicators.length > 0 
      ? mfo.successIndicators.join("\n") 
      : "No success indicators defined";

    const { data: cardData, error: cardError } = await supabase
      .from("kanban_cards")
      .insert({
        title: `${mfo.code}: ${mfo.title}`,
        description: description,
        column_id: columns[0].id,
        priority: mfo.priority,
        due_date: mfo.dueDate,
        progress: mfo.progress || 0,
        department_id: mfo.departmentId,
        mfo_id: data.id
      })
      .select()
      .single();

    if (cardError) {
      console.error("Error creating kanban card:", cardError);
      throw cardError;
    }

  

    // Add collaborators to the Kanban card
    if (mfo.collaborators && mfo.collaborators.length > 0) {
      const assigneesToInsert = mfo.collaborators.map(collaborator => ({
        card_id: cardData.id,
        employee_id: collaborator.id
      }));

 
      const { error: assigneeError } = await supabase
        .from("kanban_card_assignees")
        .insert(assigneesToInsert);

      if (assigneeError) {
        console.error("Error adding card assignees:", assigneeError);
      }
    }

    // Create success indicators
    if (mfo.successIndicators && mfo.successIndicators.length > 0) {
      const indicatorsToInsert = mfo.successIndicators.map((indicator) => ({
        mfo_id: data.id,
        indicator,
      }));

      
      const { error: indicatorError } = await supabase
        .from("mfo_success_indicators")
        .insert(indicatorsToInsert);

      if (indicatorError) {
        console.error("Error adding success indicators:", indicatorError);
        throw indicatorError;
      }
    }

    // Add collaborators to MFO
    if (mfo.collaborators && mfo.collaborators.length > 0) {
      const collaboratorsToInsert = mfo.collaborators.map((collaborator) => ({
        mfo_id: data.id,
        employee_id: collaborator.id,
      }));


      const { error: collaboratorError } = await supabase
        .from("mfo_assignees")
        .insert(collaboratorsToInsert);

      if (collaboratorError) {
        console.error("Error adding collaborators:", collaboratorError);
        throw collaboratorError;
      }
    }

    // Create custom subtasks for this MFO if provided
    if (mfo.subtasks && mfo.subtasks.length > 0) {
      const subtasksToInsert = mfo.subtasks.map((subtask) => ({
        mfo_id: data.id,
        text: subtask.text,
        completed: subtask.completed || false
      }));

     
      const { error: subtaskError } = await supabase
        .from("mfo_subtasks")
        .insert(subtasksToInsert);

      if (subtaskError) {
        console.error("Error creating subtasks:", subtaskError);
        throw subtaskError;
      }
    } else {
      // Create default subtasks only if custom subtasks aren't provided
    
      await createDefaultSubtasksForMFO(data.id);
    }

    // Create notification for MFO creation
    try {
      const { data: userData } = await supabase.auth.getSession();
      const userId = userData.session?.user.id;

      if (userId) {

        const { error: notificationError } = await supabase
          .from("notifications")
          .insert([
            {
              user_id: userId,
              title: "MFO Created",
              message: `You created a new MFO: ${mfo.code} - ${mfo.title}`,
              type: "mfo_created",
              is_read: false
            }
          ]);

        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      }

      // Also send notifications to all collaborators
      if (mfo.collaborators && mfo.collaborators.length > 0) {
        const employeeIds = mfo.collaborators.map(c => c.id);
        const { data: employees, error: employeeError } = await supabase
          .from("employees")
          .select("id, email")
          .in("id", employeeIds);

        if (!employeeError && employees) {
          const notifications = employees.map(employee => ({
            user_id: employee.id,
            email: employee.email,
            title: "New MFO Assignment",
            message: `You have been assigned to a new MFO: ${mfo.code} - ${mfo.title}`,
            type: "mfo_assigned",
            is_read: false
          }));

          if (notifications.length > 0) {

            const { error: notifyError } = await supabase
              .from("notifications")
              .insert(notifications);

            if (notifyError) {
              console.error("Error notifying assignees:", notifyError);
            }
          }
        }
      }
    } catch (notificationError) {
      console.error("Failed to create notification:", notificationError);
    }


    return data.id;
  } catch (error) {
    console.error("Error creating MFO:", error);
    throw error;
  }
};

export const updateMFO = async (
  id: string,
  mfo: Partial<{
    code: string;
    title: string;
    departmentId: string;
    budget: number;
    priority: "high" | "medium" | "low" | string;
    dueDate: string;
    status: "not-started" | "in-progress" | "delayed" | "completed" | string;
    progress: number;
    timeline: { q1: boolean; q2: boolean; q3: boolean; q4: boolean };
    successIndicators: string[];
    collaborators: TaskAssignee[];
    subtasks: { id?: string; text: string; completed: boolean }[];
  }>
): Promise<void> => {
  try {
    const updates: any = {};

    if (mfo.code) updates.code = mfo.code;
    if (mfo.title) updates.title = mfo.title;
    if (mfo.departmentId) updates.department_id = mfo.departmentId;
    if (mfo.budget) updates.budget = mfo.budget;
    if (mfo.priority) updates.priority = mfo.priority;
    if (mfo.dueDate) updates.due_date = mfo.dueDate;
    if (mfo.status) updates.status = mfo.status;
    if (mfo.progress !== undefined) updates.progress = mfo.progress;
    
    if (mfo.timeline) {
      if (mfo.timeline.q1 !== undefined) updates.q1 = mfo.timeline.q1;
      if (mfo.timeline.q2 !== undefined) updates.q2 = mfo.timeline.q2;
      if (mfo.timeline.q3 !== undefined) updates.q3 = mfo.timeline.q3;
      if (mfo.timeline.q4 !== undefined) updates.q4 = mfo.timeline.q4;
    }

    // Update the MFO basic info
    const { error } = await supabase
      .from("mfo_items")
      .update(updates)
      .eq("id", id);

    if (error) throw error;
    
    // If status changed, update the corresponding kanban card's column
    if (mfo.status) {
      const statusToColumnMap: Record<string, string> = {
        "not-started": "todo",
        "in-progress": "in-progress",
        "delayed": "delayed",
        "completed": "completed"
      };
      
      const columnName = statusToColumnMap[mfo.status] || "todo";
      
      // Find the column ID for the status
      const { data: columns, error: columnError } = await supabase
        .from("kanban_columns")
        .select("id, title")
        .ilike("title", `%${columnName}%`)
        .limit(1);
        
      if (!columnError && columns && columns.length > 0) {
        // Find and update the kanban card for this MFO
        const { error: cardUpdateError } = await supabase
          .from("kanban_cards")
          .update({ column_id: columns[0].id })
          .eq("mfo_id", id);
          
        if (cardUpdateError) {
          console.error("Error updating kanban card column:", cardUpdateError);
        }
      }
    }
    
    // If progress or title changed, update the kanban card
    if (mfo.progress !== undefined || mfo.title || mfo.priority) {
      const updates: any = {};
      if (mfo.progress !== undefined) updates.progress = mfo.progress;
      if (mfo.title) updates.title = mfo.title;
      if (mfo.priority) updates.priority = mfo.priority;
      
      const { error: cardUpdateError } = await supabase
        .from("kanban_cards")
        .update(updates)
        .eq("mfo_id", id);
        
      if (cardUpdateError) {
        console.error("Error updating kanban card:", cardUpdateError);
      }
    }

    // Update success indicators if provided
    if (mfo.successIndicators) {
      // First delete existing indicators
      const { error: deleteError } = await supabase
        .from("mfo_success_indicators")
        .delete()
        .eq("mfo_id", id);

      if (deleteError) throw deleteError;

      // Then add new indicators
      if (mfo.successIndicators.length > 0) {
        const indicatorsToInsert = mfo.successIndicators.map((indicator) => ({
          mfo_id: id,
          indicator,
        }));

        const { error: insertError } = await supabase
          .from("mfo_success_indicators")
          .insert(indicatorsToInsert);

        if (insertError) throw insertError;
      }
    }

    // Update collaborators if provided
    if (mfo.collaborators) {
      // First delete existing collaborators
      const { error: deleteError } = await supabase
        .from("mfo_assignees")
        .delete()
        .eq("mfo_id", id);

      if (deleteError) throw deleteError;

      // Then add new collaborators
      if (mfo.collaborators.length > 0) {
        const collaboratorsToInsert = mfo.collaborators.map((collaborator) => ({
          mfo_id: id,
          employee_id: collaborator.id,
        }));

        const { error: insertError } = await supabase
          .from("mfo_assignees")
          .insert(collaboratorsToInsert);

        if (insertError) throw insertError;
      }
    }

    // Update subtasks if provided
    if (mfo.subtasks) {
      // Delete existing subtasks
      const { error: deleteError } = await supabase
        .from("mfo_subtasks")
        .delete()
        .eq("mfo_id", id);

      if (deleteError) throw deleteError;

      // Insert new subtasks
      if (mfo.subtasks.length > 0) {
        const subtasksToInsert = mfo.subtasks.map((subtask) => ({
          mfo_id: id,
          text: subtask.text,
          completed: subtask.completed || false
        }));

        const { error: insertError } = await supabase
          .from("mfo_subtasks")
          .insert(subtasksToInsert);

        if (insertError) throw insertError;
      }
    }

    // Create notification for MFO update
    try {
      const { data: userData } = await supabase.auth.getSession();
      const userId = userData.session?.user.id;
      
      if (userId) {
        let notificationType = "mfo_updated";
        let notificationTitle = "MFO Updated";
        let notificationMessage = `You updated an MFO`;
        
        // If updating status, send a specific notification for that
        if (mfo.status) {
          notificationType = "mfo_status_changed";
          notificationTitle = "MFO Status Changed";
          notificationMessage = `You changed the status of an MFO to ${mfo.status}`;
        }
        
        // If updating progress, send a specific notification for that
        if (mfo.progress !== undefined) {
          notificationType = "mfo_progress_updated";
          notificationTitle = "MFO Progress Updated";
          notificationMessage = `You updated the progress of an MFO to ${mfo.progress}%`;
        }
        
        const { error: notificationError } = await supabase
          .from("notifications")
          .insert([
            {
              user_id: userId,
              title: notificationTitle,
              message: notificationMessage,
              type: notificationType,
              is_read: false
            }
          ]);
          
        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      }
    } catch (notificationError) {
      console.error("Failed to create notification:", notificationError);
    }
  } catch (error) {
    console.error("Error updating MFO:", error);
    throw error;
  }
};

export const deleteMFO = async (id: string): Promise<void> => {
  try {
    // First delete any linked kanban cards
    const { error: cardError } = await supabase
      .from("kanban_cards")
      .delete()
      .eq("mfo_id", id);
      
    if (cardError) {
      console.error("Error deleting linked kanban cards:", cardError);
    }
    
    // Then delete the MFO
    const { error } = await supabase.from("mfo_items").delete().eq("id", id);

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting MFO:", error);
    throw error;
  }
};

// Re-export the createDefaultSubtasksForMFO function from subtaskUtils
export { createDefaultSubtasksForMFO } from "./subtaskUtils";
